import React, {
  FC, Fragment, useCallback, useMemo, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import Icon from 'src/common/components/IconDS22';
import styles from './NestedMenu.module.scss';
import type { NavItemType } from '../Navigation/types';
import NavItem from '../Navigation/NavItem';

export type NestedMenuProps = {
  items: NavItemType[];
  translationNamespace?: string | string[];
  variant?: 'dropdown' | 'sidebar';
};

const traverseItems = (
  items: NavItemType[],
  callback: (item: NavItemType, parent: NavItemType) => void,
  parent?: NavItemType,
) => {
  items.forEach((item) => {
    if ('i18nKey' in item) {
      callback(item, parent || item);
      if (item.children) {
        traverseItems(item.children, callback, item);
      }
    }
  });
};

const NestedMenu: FC<NestedMenuProps> = ({
  items,
  translationNamespace = 'destination-navigation',
  variant,
}) => {
  const [t] = useTranslation(translationNamespace);
  const [activeItem, setActiveItem] = useState<NavItemType>(items[0]);
  const handleClick = useCallback((item: NavItemType) => {
    setActiveItem(item);
  }, []);
  const onBackClick = useCallback(() => {
    traverseItems(items, (item, parent) => {
      if (item.i18nKey === activeItem.i18nKey) {
        setActiveItem(parent);
      }
    });
  }, [items, activeItem.i18nKey]);
  const activeItemChildren = useMemo(() => activeItem?.children || [], [activeItem]);
  const rootKey = (items[0]).i18nKey;
  const atRoot = useMemo(() => activeItem.i18nKey === rootKey, [activeItem, rootKey]);
  let namespaceToUse = Array.isArray(translationNamespace)
    ? translationNamespace[0]
    : translationNamespace;
  namespaceToUse = activeItem.itemTranslationNamespace || namespaceToUse;
  const title = t(`${namespaceToUse}:${activeItem.i18nKey}`);

  return (
    <li className={getClassNames(styles, 'root', variant)}>
      {!atRoot && (
        <button type="button" className={styles.backTitle} onClick={onBackClick}>
          <Icon id="arrow-left" />
          {title}
        </button>
      )}
      <ul className={styles.list}>
        {activeItemChildren.map((item) => (
          <Fragment key={'i18nKey' in item ? item.i18nKey : item.key}>
            {'i18nKey' in item && item.children && (
              <li className={getClassNames(styles, 'item', classNames({ sectionDivider: item.sectionDivider }))}>
                <button type="button" onClick={() => handleClick(item)}>
                  {item.icon && <Icon id={item.icon} />}
                  {t(`${namespaceToUse}:${item.i18nKey}`)}
                  <Icon id="caret-right" size="s" />
                </button>
              </li>
            )}
            {'i18nKey' in item && 'path' in item && (
              <li className={getClassNames(styles, 'item', classNames({ sectionDivider: item.sectionDivider }))}>
                <NavItem
                  to={item.path}
                  icon={item.icon}
                  openInNewTab={item.openInNewTab}
                  variant="subNav"
                  disabled={item.disabled}
                  classNameModifier="subNav"
                >
                  {t(`${namespaceToUse}:${item.i18nKey ?? ''}`)}
                </NavItem>
              </li>
            )}
            {'component' in item && item.component && (
              <li className={getClassNames(styles, 'item', classNames({ sectionDivider: item?.sectionDivider }))}>
                {item.component}
              </li>
            )}
          </Fragment>
        ))}
      </ul>
    </li>
  );
};

export default NestedMenu;
